import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilterBar from "./components/FilterBar";
import CustomTable from "../components/CustomTable";
import { toast } from "react-toastify";
import { listFileService } from "../../../services/api/listFileService";
import { AuthContext } from "../../../contexts/AuthContext";
import Loading from "../components/Loading";
import MobilePatternHeader from "../components/MobilePatternHeader";
import { formatDate } from "../../../utils/DateFormat";
import TableSkeleton from "../components/TableSkeleton";
import { StatusBadge } from "../../../constants/statusConstants";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const parseNumber = (v, def = 1) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : def;
};

const PLACEHOLDER_IMG = "/images/default-profile.png";

const DocumentManage = ({ isMobile }) => {
  const { token_client } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL state
  const page = parseNumber(searchParams.get("page") || "1", 1);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("fromDate") || "");
  const [toDate, setToDate] = useState(searchParams.get("toDate") || "");

  // Data state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(page);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  //  Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Lazy load sentinel
  const sentinelRef = useRef(null);
  const abortRef = useRef(null);

  const formatData = useCallback(
    (apiResponse, pageNum) =>
      (apiResponse || []).map((item, index) => {
        const logo = Array.isArray(item.logo) ? item.logo[0] : null;
        const imageUrl =
          (logo?.isImage && logo?.value) || item?.image?.url || PLACEHOLDER_IMG;

        return {
          id: itemsPerPage * (pageNum - 1) + index + 1,
          _id: item._id,
          createdAt: item.createdDate,
          date: formatDate(item.createdDate),
          brand: item.brand || "--",
          profileCode: item.profileCode || "--",
          type: item.groupNames?.join("; ") || "--",
          certificates: item.numberOfCertificates || "--",
          activeDate: formatDate(item.dateActive),
          owner: Array.isArray(item.owner)
            ? item.owner[0] || "--"
            : item.owner || "--",
          image: imageUrl,
          nav_id: item._id,
          delete_id: item._id,
          isDraft: item.isDraft,
          status: item.status || "--",
        };
      }),
    [itemsPerPage],
  );

  const fetchData = useCallback(
    async (
      search_value = searchParams.get("search") || "",
      fromDate_value = searchParams.get("fromDate") || "",
      toDate_value = searchParams.get("toDate") || "",
      pageNum = page,
      limit = itemsPerPage,
      append = false,
    ) => {
      try {
        if (!append) setLoading(true);

        if (abortRef.current) {
          try {
            abortRef.current.abort();
          } catch {}
        }
        abortRef.current = new AbortController();

        const response = await listFileService.get_all(
          search_value,
          fromDate_value,
          toDate_value,
          pageNum,
          limit,
          token_client,
        );

        const formatted = formatData(response?.data || [], pageNum);
        setData((prev) => (append ? [...prev, ...formatted] : formatted));
        setTotalPages(response?.pagination?.totalPages || 1);
        setCurrentPage(Number(response?.pagination?.currentPage) || page);
      } catch (error) {
        toast.dismiss();
        console.error("Lỗi khi lấy hồ sơ:", error);
        toast.error("Lỗi khi lấy dữ liệu từ server");
        setData([]);
        setTotalPages(1);
        setCurrentPage(1);
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
        setLoadingMore(false);
      }
    },
    [token_client, formatData, itemsPerPage, page, searchParams],
  );

  const handleSearch = useCallback(() => {
    if (toDate && !fromDate) return toast.error("Vui lòng nhập ngày bắt đầu.");
    if (fromDate && !toDate) return toast.error("Vui lòng nhập ngày kết thúc.");

    const from = fromDate
      ? new Date(fromDate.split("-").reverse().join("-"))
      : null;
    const to = toDate ? new Date(toDate.split("-").reverse().join("-")) : null;
    if (from && to && from > to) {
      return toast.error("Ngày bắt đầu lớn hơn ngày kết thúc.");
    }

    const newParams = new URLSearchParams();
    newParams.set("page", "1");
    if (search.trim()) newParams.set("search", search.trim());
    if (fromDate) newParams.set("fromDate", fromDate);
    if (toDate) newParams.set("toDate", toDate);
    setSearchParams(newParams);
  }, [fromDate, toDate, search, setSearchParams]);

  const handlePageChange = useCallback(
    (newPage) => {
      const n = parseNumber(newPage, 1);
      const p = Math.min(Math.max(n, 1), totalPages);
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("page", String(p));
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, totalPages],
  );

  // Hàm mở modal xác nhận xóa
  const handleDelete = useCallback(
    async (idOrItem) => {
      const item =
        typeof idOrItem === "object"
          ? idOrItem
          : data.find((x) => x.delete_id === idOrItem || x.nav_id === idOrItem);

      if (!item) return toast.error("Không tìm thấy hồ sơ.");

      const isChoDuyet = item.status === "Chờ duyệt";
      if (item.isDraft === false && !isChoDuyet) {
        toast.error("Chỉ được xóa hồ sơ nháp hoặc 'Chờ duyệt'");
        return;
      }

      setItemToDelete(item);
      setShowDeleteModal(true);
    },
    [data],
  );

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      setShowDeleteModal(false);
      await listFileService.delete(itemToDelete._id, token_client);
      toast.success("Đã xóa hồ sơ!");
      fetchData();
    } catch (error) {
      toast.dismiss();
      console.error("Lỗi khi xóa hồ sơ:", error);
      toast.error("Lỗi khi xóa!");
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
      setItemToDelete(null);
    }
  }, [itemToDelete, fetchData, token_client]);

  const handleDuplicate = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const res = await listFileService.duplicate_profile(id, token_client);
        toast.success("Đã nhân bản!");
        navigate(`/quan-ly-ho-so/ho-so/${res.profile._id}/chinh-sua`);
      } catch (error) {
        toast.dismiss();
        console.error("Lỗi khi nhân bản hồ sơ:", error);
        toast.error("Lỗi khi nhân bản!");
      } finally {
        setLoading(false);
        setHasLoadedOnce(true);
      }
    },
    [navigate, token_client],
  );

  // Lazy load mobile: IntersectionObserver
  useEffect(() => {
    if (!isMobile) return;
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !loading &&
          !loadingMore &&
          currentPage < totalPages
        ) {
          const nextPage = currentPage + 1;
          setLoadingMore(true);
          fetchData(search, fromDate, toDate, nextPage, itemsPerPage, true);
          setCurrentPage(nextPage);
        }
      },
      { root: null, rootMargin: "0px 0px 200px 0px", threshold: 0.01 },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [
    isMobile,
    loading,
    loadingMore,
    currentPage,
    totalPages,
    fetchData,
    search,
    fromDate,
    toDate,
    itemsPerPage,
  ]);

  // Fetch lần đầu & khi URL biến đổi
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchParams]);

  // Columns (desktop)
  const columns = useMemo(
    () => [
      { key: "id", title: "STT", minWidth: "64px", align: "text-center" },
      {
        key: "image",
        title: "Mẫu",
        minWidth: "112px",
        align: "text-center",
        render: (value) => (
          <div className="flex items-center justify-center">
            <img
              src={value || PLACEHOLDER_IMG}
              onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
              className="size-20 object-contain rounded-md ring-1 ring-slate-100"
              alt="Document Logo"
            />
          </div>
        ),
      },
      { key: "brand", title: "Tên", minWidth: "220px" },
      { key: "profileCode", title: "Số đơn", minWidth: "160px" },
      { key: "date", title: "Ngày nộp đơn", minWidth: "150px" },
      { key: "type", title: "Phân loại", minWidth: "120px" },
      { key: "owner", title: "Chủ đơn", minWidth: "200px" },
      {
        key: "status",
        title: "Trạng thái",
        minWidth: "160px",
        align: "text-center",
        render: (status) => <StatusBadge status={status} />,
      },
    ],
    [],
  );

  // Lần tải đầu: show Loading (desktop & mobile)
  if (loading && !loadingMore && !hasLoadedOnce) {
    return isMobile ? (
      <div className="flex items-center justify-center h-screen w-screen">
        <Loading />
      </div>
    ) : (
      <div className="space-y-6 h-full overflow-auto">
        <FilterBar
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          search={search}
          setSearch={setSearch}
          handleSearch={handleSearch}
        />
        <TableSkeleton rows={3} />
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="h-full w-full">
        <MobilePatternHeader
          title="Quản lý hồ sơ"
          onBack={() => navigate(-1)}
        />

        <div className="h-[calc(100%-64px)] bg-[#F2F2F2] p-3 pr-0">
          <div className="relative w-full flex items-center pr-3">
            <input
              type="text"
              placeholder="Bạn đang muốn tìm kiếm..."
              className="border border-gray-200 rounded-3xl px-4 py-2 focus:outline-blue-500 w-full"
              value={search}
              autoComplete="off"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <span
              className="absolute right-6 cursor-pointer active:scale-[0.98]"
              onClick={handleSearch}
              aria-label="Search"
            >
              <img src="/icons/search-black.svg" alt="Search icon" />
            </span>
          </div>

          <div className="h-screen max-h-[calc(100%-120px)] overflow-auto overflow-x-hidden pr-3">
            <div className="space-y-3 mt-3">
              {Array.isArray(data) && data.length > 0 ? (
                data.map((doc, idx) => (
                  <div
                    key={doc.nav_id || idx}
                    className="w-full bg-white rounded-md flex flex-col items-center justify-start p-3 border border-[#EAEAEA] hover:bg-[#f9f9f9] transition-colors motion-safe:animate-[fadeInUp_320ms_ease-out] cursor-pointer"
                    onClick={() =>
                      navigate(`/quan-ly-ho-so/ho-so/${doc.nav_id}`)
                    }
                    style={{ animationDelay: `${(idx % 10) * 20}ms` }}
                  >
                    <div className="flex items-center gap-4 w-full">
                      <img
                        src={doc.image || PLACEHOLDER_IMG}
                        alt="Hình ảnh hồ sơ"
                        className="size-20 object-contain rounded-md ring-1 ring-slate-100"
                        onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
                      />
                      <div className="w-[60%] sm:w-[80%] md:w-full text-sm truncate">
                        <p className="truncate">{doc.brand}</p>
                        <p className="truncate">
                          {doc.profileCode ? doc.profileCode : "--"}
                        </p>
                        <p className="truncate">Ngày: {doc.date || "--"}</p>
                        <p className="truncate">
                          Phân loại: {doc.type || "--"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : hasLoadedOnce ? (
                <div className="flex items-center justify-center h-[50vh]">
                  <img src="/images/empty.png" alt="Empty icon" />
                </div>
              ) : null}

              {/* Loading more */}
              {loadingMore && (
                <div className="text-center py-4 text-sm text-gray-500">
                  Đang tải thêm dữ liệu...
                </div>
              )}

              {/* No more */}
              {!loadingMore && currentPage >= totalPages && data.length > 0 && (
                <div className="text-center py-4 text-sm text-gray-400">
                  Không còn dữ liệu nào để hiển thị.
                </div>
              )}

              {/* Sentinel */}
              <div ref={sentinelRef} className="h-2" />
            </div>
          </div>
        </div>

        {/* Modal xác nhận xóa */}
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setItemToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="localhost:3001 says"
          message="Bạn có chắc chắn muốn xóa không?"
        />

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to   { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.2s ease-out;
          }
        `}</style>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="space-y-6 h-full flex flex-col">
      <FilterBar
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        search={search}
        setSearch={setSearch}
        handleSearch={handleSearch}
      />
      <CustomTable
        data={data}
        columns={columns}
        currentPage={page}
        setCurrentPage={handlePageChange}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        setView
        setDelete={(item) => item.isDraft}
        setDuplicate
        basePath="/quan-ly-ho-so/ho-so"
        handleDelete={handleDelete}
        handleDuplicate={handleDuplicate}
        onRowClick={(item) => navigate(`/quan-ly-ho-so/ho-so/${item._id}`)}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Xác nhận xóa hồ sơ"
        message="Bạn có chắc chắn muốn xóa không?"
      />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default DocumentManage;
